import { hasItem } from './utils/itemList.js';
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Navigation from 'flarum/common/components/Navigation';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import { openCanonicalNewDiscussion } from './utils/openCanonicalNewDiscussion';
import LinkButton from 'flarum/common/components/LinkButton';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import ItemList from 'flarum/common/utils/ItemList';
import CenterQuickRail from './components/CenterQuickRail';
import { discussionCenterUsesTechnicianIcon, resolveDiscussionBoardTarget, resolveDiscussionBrandAccessibleLabel, resolveDiscussionBrandTitle } from './utils/discussionBrandTitle';
import { listDiscussionBrandBoards } from './utils/discussionBrandDropdownItems';
import { brandHref, getNavigationManifest, tagHref } from './utils/manifest';
import { resolveBoardStructuralBack } from './utils/boardStructuralBack';
import { resolveIndexBoardSlug } from './utils/currentBoardContext';
import { recordOpenBoardAtTopIntent, consumeOpenBoardAtTopIntent } from './utils/boardBackIntent';
import icon from './utils/icon';
import { TECHNICIAN_TOPICS_ICON } from './utils/startNav';
function currentDiscussionBoardTarget() {
  const current = app.current;
  if (!current || typeof current.matches !== 'function' || !current.matches(DiscussionPage)) {
    return null;
  }
  const discussion = typeof current.get === 'function' ? current.get('discussion') : null;
  return resolveDiscussionBoardTarget({
    discussion,
    manifest: getNavigationManifest()
  });
}
function currentIndexRouteName() {
  const current = app.current;
  if (!current || typeof current.get !== 'function') return '';
  return String(current.get('routeName') || '');
}
function currentIndexBoardSlug() {
  const current = app.current;
  const isDiscussionPage = !!(current && typeof current.matches === 'function' && current.matches(DiscussionPage));
  const isIndexPage = !!(current && typeof current.matches === 'function' && current.matches(IndexPage) && !isDiscussionPage);
  const currentTag = typeof app.currentTag === 'function' ? app.currentTag() : null;
  let routeTagParam = '';
  try {
    routeTagParam = m.route.param('tags') || '';
  } catch (error) {
    routeTagParam = '';
  }
  const stickyState = app.search && app.search.state && typeof app.search.state.stickyParams === 'function' ? app.search.state.stickyParams() : null;
  const stickyDirect = app.search && typeof app.search.stickyParams === 'function' ? app.search.stickyParams() : {};
  const stickyTags = stickyState && stickyState.tags || stickyDirect && stickyDirect.tags || '';
  return resolveIndexBoardSlug({
    isIndexPage,
    isDiscussionPage,
    currentTag,
    routeTagParam,
    stickyTags
  });
}
function boardMainBackButton() {
  return m(LinkButton, {
    className: "Button Navigation-back Button--icon FlatRateBoardBackToMain",
    href: "/",
    icon: "fas fa-chevron-left",
    "aria-label": "Back to MAIN",
    force: true
  });
}
function discussionBoardBackButton(target) {
  // Explicit force documents the Mithril route-remount contract required for
  // board transitions (URL change alone is not acceptance).
  return m(LinkButton, {
    className: "Button Navigation-back Button--icon FlatRateDiscussionBackToBoard",
    href: tagHref(target.slug),
    icon: "fas fa-chevron-left",
    "aria-label": `Back to ${target.name}`,
    force: true,
    onclick: () => {
      recordOpenBoardAtTopIntent();
    }
  });
}
app.initializers.add('flatrate-discussion-center-menu', () => {
  // Flarum 1.8.19 uses the PostStreamScrubber as DiscussionPage's mobile
  // App-titleControl. Add a dedicated center dropdown instead. CSS hides the
  // scrubber only on phones, so desktop post navigation stays unchanged.
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    const manifest = getNavigationManifest();
    if (!manifest || !this.discussion) {
      return;
    }
    const boardTarget = resolveDiscussionBoardTarget({
      discussion: this.discussion,
      manifest
    });
    const visibleTitle = resolveDiscussionBrandTitle({
      discussion: this.discussion,
      manifest
    });
    const accessibleLabel = resolveDiscussionBrandAccessibleLabel({
      discussion: this.discussion,
      manifest
    });
    const technicianTitle = discussionCenterUsesTechnicianIcon(boardTarget);
    const pickerItems = new ItemList();
    pickerItems.add('flatrateQuickRail', m(CenterQuickRail, {
      manifest: manifest,
      page: this
    }), 200);

    // SelectDropdown styles only li > a|button. Do not nest PresentationNav
    // (a <div> tree) here — production left item-flatratePresentationNav empty.
    // Reuse the canonical Brand manifest via LinkButton children instead.
    listDiscussionBrandBoards(manifest).forEach((board, index) => {
      pickerItems.add(`brand-${board.boardKey}`, m(LinkButton, {
        className: `Button--flat FlatRateDiscussionPicker-link FlatRateDiscussionBrandLink depth-${board.depth}`,
        href: brandHref(board),
        force: true
      }, m("span", {
        className: "FlatRateDiscussionBrandName"
      }, board.name)), -14 - index);
    });
    if (hasItem(items, 'flatrateDiscussionBrandPicker')) {
      items.remove('flatrateDiscussionBrandPicker');
    }
    items.add('flatrateDiscussionBrandPicker', m(SelectDropdown, {
      buttonClassName: "Button",
      className: `App-titleControl FlatRateDiscussionBrandPicker ${technicianTitle ? 'FlatRatePresentationTitle--technician' : 'FlatRatePresentationTitle--contextual'}`,
      accessibleToggleLabel: accessibleLabel,
      defaultLabel: visibleTitle
    }, pickerItems.toArray()), -90);
  });

  // Use stable structural destinations for both the F2 history and drawer
  // control seams. Do not
  // invoke Flarum app-history back helpers — the board route is the destination.
  override(Navigation.prototype, 'getBackButton', function (original) {
    const target = currentDiscussionBoardTarget();
    if (target) {
      return discussionBoardBackButton(target);
    }
    const boardBack = resolveBoardStructuralBack({
      slug: currentIndexBoardSlug(),
      routeName: currentIndexRouteName(),
      manifest: getNavigationManifest()
    });
    if (boardBack) {
      return boardMainBackButton();
    }
    return original();
  });
  override(Navigation.prototype, 'getDrawerButton', function (original) {
    const target = currentDiscussionBoardTarget();
    if (target) {
      return discussionBoardBackButton(target);
    }
    const boardBack = resolveBoardStructuralBack({
      slug: currentIndexBoardSlug(),
      routeName: currentIndexRouteName(),
      manifest: getNavigationManifest()
    });
    if (boardBack) {
      return boardMainBackButton();
    }
    return original();
  });

  // F2 renders drawer and history Back together. On structural board routes
  // both overridden controls point to the same destination; keep one arrow.
  extend(Navigation.prototype, 'items', function (items) {
    if (!this.attrs.drawer || !hasItem(items, 'drawer')) return;
    const target = currentDiscussionBoardTarget() || resolveBoardStructuralBack({
      slug: currentIndexBoardSlug(),
      routeName: currentIndexRouteName(),
      manifest: getNavigationManifest()
    });
    if (target) items.remove('back');
  });
  override(SelectDropdown.prototype, 'getButtonContent', function (original, children) {
    const content = original(children);
    const className = String(this.attrs && this.attrs.className || '');
    if (!className.includes('FlatRateDiscussionBrandPicker') || !className.includes('FlatRatePresentationTitle--technician')) {
      return content;
    }
    const next = Array.isArray(content) ? content.slice() : [content];
    const hasWrench = next.some(node => String(node && node.attrs && node.attrs.className || '').includes('fa-wrench'));
    if (!hasWrench) {
      next.unshift(icon(TECHNICIAN_TOPICS_ICON));
    }
    return next;
  });

  // Custom board-arrow: one-shot open-board-at-top intent. Native Back keeps
  // lastDiscussion restoration.
  extend(IndexPage.prototype, 'oninit', function () {
    if (consumeOpenBoardAtTopIntent()) {
      this.lastDiscussion = undefined;
      this.flatrateOpenBoardAtTop = true;
    }
  });
  extend(IndexPage.prototype, 'oncreate', function () {
    if (!this.flatrateOpenBoardAtTop) {
      return;
    }
    this.flatrateOpenBoardAtTop = false;
    if (typeof window !== 'undefined' && window.jQuery) {
      window.jQuery(window).scrollTop(0);
    } else if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  });

  // Keep the phone header's right-hand primary slot available for the board
  // follow control. New Discussion moves into the list toolbar instead.
  extend(IndexSidebar.prototype, 'items', function (items) {
    if (hasItem(items, 'newDiscussion')) {
      items.remove('newDiscussion');
    }
  });

  // Replace the refresh / mark-all-read pair with one explicit compose action.
  // F2 core adds a mobile GlobalSearch button to this toolbar. Remove that
  // duplicate magnifier on MAIN and Brand boards; global Search remains in
  // the navigation drawer and desktop header.
  extend(IndexPage.prototype, 'actionItems', function (items) {
    if (hasItem(items, 'search')) {
      items.remove('search');
    }
    if (hasItem(items, 'refresh')) {
      items.remove('refresh');
    }
    if (hasItem(items, 'markAllAsRead')) {
      items.remove('markAllAsRead');
    }
    if (hasItem(items, 'newDiscussion')) {
      items.remove('newDiscussion');
    }
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    const label = app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`);
    items.add('newDiscussion', m(Button, {
      icon: "fas fa-plus",
      className: "Button Button--icon FlatRateInlineNewDiscussion",
      title: label,
      "aria-label": label,
      onclick: () => openCanonicalNewDiscussion(this).catch(() => {}),
      disabled: !canStartDiscussion
    }), 100);
  });
}, -50);